'use client';

/**
 * <ListingFilterBar> (BAL-665) — the web filter toolbar above a listing grid:
 * search field · facet row (segmented toggle / dropdown / range) · sort
 * dropdown · applied-filter chips + Clear-all + result count. Config-driven —
 * every directory renders through this; no bespoke per-surface filter UI.
 */
import type { ReactNode } from 'react';

import { SearchIcon, SparklesIcon } from 'lucide-react';

import { Input } from '@kit/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { cn } from '@kit/ui/utils';

import { AppliedFilterChips } from './applied-filter-chips';
import { FacetControl } from './facet-control';
import type { ListingFilterConfig } from './types';
import type { UseListingFiltersResult } from './use-listing-filters';

interface ListingFilterBarProps {
  config: ListingFilterConfig;
  filters: UseListingFiltersResult;
  resultCount?: number;
  /**
   * Surface-specific controls rendered at the START of the facet row — e.g. an
   * async entity multi-select (organization picker) that doesn't fit the static
   * `FilterFacet` model. Composition slot, not a config facet kind (keeps the
   * registry model free of flags — see the listing-filters convergence WIP).
   */
  leading?: ReactNode;
  /**
   * Surface-specific control rendered on its OWN row below the chips — e.g. the
   * auditions "Match my profile" personalization toggle. Owns its own URL param.
   */
  slot?: ReactNode;
  className?: string;
}

export function ListingFilterBar({
  config,
  filters,
  resultCount,
  leading,
  slot,
  className,
}: ListingFilterBarProps) {
  const { state, searchInput, setSearch, setSort, toggleFacet, setFacet, chips } =
    filters;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center gap-3">
        {/* Surface-specific leading controls (entity pickers, etc.) */}
        {leading}

        {/* Facets */}
        {config.facets.map((facet) => (
          <FacetControl
            key={facet.key}
            facet={facet}
            values={state.facets[facet.key] ?? []}
            onToggle={(value) => toggleFacet(facet.key, value)}
            onSetValues={(values) => setFacet(facet.key, values)}
          />
        ))}

        <div className="ml-auto flex items-center gap-3">
          {/* Sort */}
          {config.sorts.length > 0 ? (
            <Select value={state.sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {config.sorts.map((sort) => (
                  <SelectItem key={sort.value} value={sort.value}>
                    {sort.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}

          {/* Search */}
          {config.search ? (
            <div className="relative w-64 max-w-full">
              <span className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">
                {config.search.magic ? (
                  <SparklesIcon className="size-4" />
                ) : (
                  <SearchIcon className="size-4" />
                )}
              </span>
              <Input
                value={searchInput}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={config.search.placeholder}
                className="pl-8"
              />
            </div>
          ) : null}
        </div>
      </div>

      <AppliedFilterChips
        chips={chips}
        resultCount={resultCount}
        onRemove={(chip) =>
          chip.value
            ? toggleFacet(chip.facetKey, chip.value)
            : setFacet(chip.facetKey, [])
        }
        onClearAll={filters.clearAll}
      />

      {/* Surface-specific control row (e.g. personalization toggle) */}
      {slot}
    </div>
  );
}
